import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import {
  CONTENT_REPORT_REASONS,
  CONTENT_REPORT_REASON_LABELS,
  type ContentReportReason,
  type ContentReportTarget,
} from '@mlm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  toast,
} from '@mlm/ui';
import { reportContent } from '../lib/api';
import { useAbuseReporting } from '../lib/use-abuse-reporting';

/** Server cap on `details` — mirrored here so the counter can't promise more. */
const DETAILS_MAX = 1000;

/**
 * The one Report affordance, used on the course page, review cards, Q&A rows
 * and announcements. It renders its OWN trigger so a caller only has to say
 * what is being reported, and it renders NOTHING when the tenant has abuse
 * reporting off or the viewer is logged out (`useAbuseReporting`).
 *
 * Deliberately quiet: reporting is a rare, negative action sitting next to
 * content people are meant to read, so it is a small text button, never a CTA.
 */
export function ReportDialog({
  targetKind,
  targetId,
  label = 'Report',
  className,
}: {
  targetKind: ContentReportTarget;
  targetId: string;
  /** Trigger text — e.g. "Report course" where the noun isn't obvious. */
  label?: string;
  /** Trigger classes, so a storefront surface can match its own palette. */
  className?: string;
}) {
  const enabled = useAbuseReporting();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ContentReportReason | ''>('');
  const [details, setDetails] = useState('');

  // A fresh dialog every time: the previous target's reason must not carry
  // over onto the next thing this member opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setDetails('');
    }
  }, [open]);

  const submit = useMutation({
    meta: { errorMessage: "Couldn't send your report. Please try again in a little while." },
    mutationFn: () =>
      reportContent({
        targetKind,
        targetId,
        reason: reason as ContentReportReason,
        details: details.trim() || undefined,
      }),
    onSuccess: (res) => {
      // The wording depends on the RESPONSE — a repeat report is answered with
      // `alreadyReported` rather than an error — and `meta.successMessage` is a
      // fixed string, so this one toast is raised by hand.
      if (res.alreadyReported) {
        toast.info('You already reported this. Our team is looking at it.');
      } else {
        toast.success('Thanks — your report is with our team.');
      }
      setOpen(false);
    },
  });

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline',
          className,
        )}
      >
        <Flag className="h-3 w-3" />
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              Tell us what's wrong and our team will review it. Your name is not shown to whoever
              posted this.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Reason</label>
              <Select value={reason} onValueChange={(v) => setReason(v as ContentReportReason)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a reason" />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_REPORT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {CONTENT_REPORT_REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="report-details">
                Details (optional)
              </label>
              <Textarea
                id="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={DETAILS_MAX}
                rows={4}
                placeholder="What should the reviewer look at?"
              />
              <p className="text-right text-[11px] text-muted-foreground">
                {details.length}/{DETAILS_MAX}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submit.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submit.mutate()} disabled={!reason || submit.isPending}>
              {submit.isPending ? 'Sending…' : 'Send report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
